import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SchemaJsonLd from "@/components/base/SchemaJsonLd";
import { getArticlesByCategory, getCategoryBySlug } from "@/utils/news";
import CategoryList from "@/pages/category/components/CategoryList";
import CategorySidebar from "@/pages/category/components/CategorySidebar";

export default function CategoryPage() {
  const { slug } = useParams();
  const category = slug ? getCategoryBySlug(slug) : undefined;
  const items = slug ? getArticlesByCategory(slug) : [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!category || !slug) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Header />
        <main className="flex flex-1 items-center justify-center px-4 py-24 text-center">
          <div>
            <i className="ri-folder-open-line text-5xl text-foreground-300" />
            <h1 className="mt-5 font-serif-cn text-2xl font-bold text-foreground-950">
              没有找到这个栏目
            </h1>
            <p className="mt-3 text-sm text-foreground-600">
              你可能输入了错误的地址，回到首页看看今天的热点吧。
            </p>
            <Link
              to="/"
              className="mt-6 inline-block cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition hover:bg-primary-600"
            >
              回到首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        name: `${category.name} - 51cg网`,
        description: category.desc,
        inLanguage: "zh-CN",
      },
      {
        "@type": "ItemList",
        name: category.name,
        itemListElement: items.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `https://ryvucy.cn/article/${item.id}`,
          name: item.title,
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://ryvucy.cn/" },
          { "@type": "ListItem", position: 2, name: category.name },
        ],
      },
    ],
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Header />
      <main className="flex-1">
        <SchemaJsonLd data={schema} />

        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-[1400px] px-4 py-8 md:px-6">
            <Breadcrumb items={[{ label: category.name }]} />
            <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                  <i className={`${category.icon} text-2xl`} />
                </span>
                <div>
                  <h1 className="font-serif-cn text-2xl font-bold text-foreground-950 md:text-3xl">
                    {category.name}
                  </h1>
                  <p className="mt-1 text-sm text-foreground-600">{category.desc}</p>
                </div>
              </div>
              <p className="flex items-center gap-1.5 text-sm text-foreground-500">
                <i className="ri-refresh-line" />
                持续更新 · 共 {items.length} 条
              </p>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-[1400px] px-4 py-8 md:px-6">
          <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10">
            <CategoryList items={items} />
            <div className="lg:sticky lg:top-36">
              <CategorySidebar currentSlug={slug} />
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}